import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Popper,
  List,
  ListItemButton,
  ListItemText,
  ClickAwayListener,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Note as NoteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  EmojiEvents as EmojiEventsIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Autorenew as AutorenewIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Send as SendIcon,
  ChatBubble as ChatBubbleIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import apiClient from '../../api/client';

type NoteStatus = '' | 'open' | 'closed';
type AssignToUser = { id: number; name: string };
type NoteComment = {
  id: number | string;
  task_id?: number;
  comment?: string | null;
  comment_text?: string | null;
  content?: string | null;
  description?: string | null;
  created_at?: string | null;
  created_by_name?: string | null;
};

let cachedAssignToUsers: AssignToUser[] | null = null;
let cachedAssignToUsersAt = 0;
const ASSIGN_TO_CACHE_MS = 5 * 60 * 1000;

interface Note {
  id: number;
  module: string;
  page: string;
  start_date: string;
  end_date: string;
  title: string;
  description: string;
  // New API shape uses task_type; keep note_type for backward compatibility
  task_type?: 'general' | 'action_item' | 'issue' | 'achievement' | string;
  note_type?: 'general' | 'action_item' | 'issue' | 'achievement' | string;
  priority?: 'low' | 'medium' | 'high' | string | null;
  // API may return name or id; keep flexible
  assign_to?: string | number | null;
  assigned_to?: string | number | null;
  assigned_to_name?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  comment_count?: number | null;
  status?: NoteStatus | string | null;
  created_at: string;
  updated_at: string;
}

interface NotesPanelProps {
  module: string;
  page: string;
  startDate: string;
  endDate: string;
  permissions: {
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
    canUpload: boolean;
  };
  statusFilter?: '' | 'open' | 'closed';
  onClose?: () => void;
  onViewChange?: (view: 'list' | 'form' | 'comments') => void;
}

export const NOTES_PANEL_WIDTH = 480;

const NotesPanel: React.FC<NotesPanelProps> = ({ module, page, startDate, endDate, permissions, statusFilter = '', onClose, onViewChange }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);
  const [view, setView] = useState<'list' | 'form' | 'comments'>('list');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    note_type: 'general' as 'general' | 'action_item' | 'issue' | 'achievement',
    assign_to: '' as '' | number,
    status: '' as NoteStatus,
  });

  const [commentNote, setCommentNote] = useState<Note | null>(null);
  const [comments, setComments] = useState<NoteComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const commentsEndRef = useRef<HTMLDivElement | null>(null);
  const commentInputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  const [commentMentionOpen, setCommentMentionOpen] = useState(false);
  const [commentMentionQuery, setCommentMentionQuery] = useState('');
  const [commentMentionStart, setCommentMentionStart] = useState<number | null>(null);
  const [commentMentionCursor, setCommentMentionCursor] = useState<number | null>(null);
  const [commentMentionActiveIndex, setCommentMentionActiveIndex] = useState(0);

  const quillRef = useRef<ReactQuill | null>(null);
  const mentionWrapRef = useRef<HTMLDivElement | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [mentionPos, setMentionPos] = useState<{ top: number; left: number } | null>(null);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);

  const [assignToUsers, setAssignToUsers] = useState<AssignToUser[]>(cachedAssignToUsers || []);
  const [assignToLoading, setAssignToLoading] = useState(false);
  const [assignToError, setAssignToError] = useState<string | null>(null);

  useEffect(() => {
    onViewChange?.(view);
  }, [view, onViewChange]);

  const fetchAssignToUsers = useCallback(async () => {
    const now = Date.now();
    if (cachedAssignToUsers && now - cachedAssignToUsersAt < ASSIGN_TO_CACHE_MS) {
      setAssignToUsers(cachedAssignToUsers);
      return;
    }
    setAssignToLoading(true);
    setAssignToError(null);
    try {
      const res = await apiClient.get('/api/v1/auth/user_names');
      const users = Array.isArray(res.data) ? (res.data as AssignToUser[]) : [];
      cachedAssignToUsers = users;
      cachedAssignToUsersAt = now;
      setAssignToUsers(users);
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to load users';
      setAssignToError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setAssignToLoading(false);
    }
  }, []);

  // Fetch Assign To options when panel loads (cached)
  useEffect(() => {
    fetchAssignToUsers();
  }, [fetchAssignToUsers]);

  // Clear assign_to/status when switching to types that don't need them
  useEffect(() => {
    const needsAssignTo = formData.note_type === 'action_item' || formData.note_type === 'issue';
    if (!needsAssignTo && (formData.assign_to || formData.status)) {
      setFormData((prev) => ({ ...prev, assign_to: '', status: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.note_type]);

  const needsWorkflowFields = formData.note_type === 'action_item' || formData.note_type === 'issue';

  const mentionCandidates = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase();
    const base = assignToUsers || [];
    const filtered = q ? base.filter((u) => u.name.toLowerCase().includes(q)) : base;
    return filtered.slice(0, 8);
  }, [assignToUsers, mentionQuery]);

  const commentMentionCandidates = useMemo(() => {
    const q = commentMentionQuery.trim().toLowerCase();
    const base = assignToUsers || [];
    const filtered = q ? base.filter((u) => u.name.toLowerCase().includes(q)) : base;
    return filtered.slice(0, 8);
  }, [assignToUsers, commentMentionQuery]);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Convert YYYY-MM format to YYYY-MM-DD format
      const formatDateForAPI = (dateStr: string) => {
        if (dateStr && dateStr.length === 7) { // YYYY-MM format
          return `${dateStr}-01`; // Add -01 for first day of month
        }
        return dateStr;
      };

      const response = await apiClient.get('/api/v1/delivery/tasks', {
        params: {
          module,
          page,
          start_date: formatDateForAPI(startDate),
          end_date: formatDateForAPI(endDate),
        },
      });
      // New response: { tasks: [...], total: number }
      setNotes(response.data.tasks || response.data.notes || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch notes';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  }, [module, page, startDate, endDate]);

  // Fetch notes when date range changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchNotes();
    }
  }, [fetchNotes, startDate, endDate]);

  const handleCreateNote = () => {
    setEditingNote(null);
    setFormData({ title: '', description: '', note_type: 'general', assign_to: '', status: '' });
    setView('form');
    fetchAssignToUsers();
  };

  const handleEditNote = (note: Note) => {
    const rawStatus = typeof note.status === 'string' ? note.status : '';
    const status: NoteStatus = rawStatus === 'open' || rawStatus === 'closed' ? rawStatus : '';

    const rawAssigned =
      typeof note.assigned_to === 'number'
        ? note.assigned_to
        : typeof note.assign_to === 'number'
          ? note.assign_to
          : '';

    const type = (note.task_type || note.note_type || 'general') as any;

    setEditingNote(note);
    setFormData({
      title: note.title,
      description: note.description,
      note_type: type,
      assign_to: rawAssigned as any,
      status: '',
    });
    setView('form');
    fetchAssignToUsers();
  };

  const handleDeleteNote = (noteId: number) => {
    setNoteToDelete(noteId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (noteToDelete === null) return;

    try {
      await apiClient.delete(`/api/v1/delivery/tasks/${noteToDelete}`);
      await fetchNotes();
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete note';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      // Close dialog even on error to avoid sticking state, or keep it open? 
      // Usually better to keep open if error is related to deletion but here we show error in main panel.
      setDeleteConfirmOpen(false);
    }
  };

  const handleSaveNote = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Title and description are required');
      return;
    }

    try {
      const payload: any = {
        title: formData.title,
        description: formData.description,
        task_type: formData.note_type,
      };

      if (needsWorkflowFields) {
        payload.assigned_to = formData.assign_to === '' ? null : formData.assign_to;
        payload.status = formData.status;
      } else {
        // Force-clear when unsupported
        payload.assigned_to = null;
        payload.status = '';
      }

      if (editingNote) {
        // Update existing note
        await apiClient.put(`/api/v1/delivery/tasks/${editingNote.id}`, {
          ...payload,
        });
      } else {
        // Create new note
        const formatDateForAPI = (dateStr: string) => {
          if (dateStr && dateStr.length === 7) { // YYYY-MM format
            return `${dateStr}-01`; // Add -01 for first day of month
          }
          return dateStr;
        };

        await apiClient.post('/api/v1/delivery/tasks', {
          module,
          page,
          start_date: formatDateForAPI(startDate),
          end_date: formatDateForAPI(endDate),
          ...payload,

        });
      }

      setView('list');
      await fetchNotes();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save note';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    }
  };

  const getNoteTypeIcon = (type: string) => {
    switch (type) {
      case 'action_item':
        return <CheckCircleIcon sx={{ color: '#10b981' }} />;
      case 'issue':
        return <ErrorIcon sx={{ color: '#ef4444' }} />;
      case 'achievement':
        return <EmojiEventsIcon sx={{ color: '#f59e0b' }} />;
      default:
        return <NoteIcon sx={{ color: '#6b7280' }} />;
    }
  };

  const getNoteTypeColor = (type: string) => {
    switch (type) {
      case 'action_item':
        return 'success';
      case 'issue':
        return 'error';
      case 'achievement':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startMonth = start.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

    if (startMonth === endMonth) {
      return startMonth;
    }
    return `${startMonth} - ${endMonth}`;
  };

  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const truncate = (text: string, max = 160) => (text.length > max ? `${text.slice(0, max).trim()}…` : text);

  const normalizeStatus = (s: any): NoteStatus => (s === 'open' || s === 'closed' ? s : '');

  const getAssignToName = (note: Note) => {
    if (note.assigned_to_name) return String(note.assigned_to_name);
    const raw = (note.assigned_to ?? note.assign_to) as any;
    if (raw == null || raw === '') return '';
    if (typeof raw === 'string') return raw;
    const u = assignToUsers.find((x) => x.id === raw);
    return u?.name || `User #${raw}`;
  };

  const parseCommentText = (c: NoteComment) =>
    String(c.comment_text ?? c.comment ?? c.content ?? c.description ?? '').trim();

  const closeCommentMention = () => {
    setCommentMentionOpen(false);
    setCommentMentionQuery('');
    setCommentMentionStart(null);
    setCommentMentionCursor(null);
    setCommentMentionActiveIndex(0);
  };

  const updateCommentMentionFromText = (text: string, cursorPos: number) => {
    const windowStart = Math.max(0, cursorPos - 48);
    const before = text.slice(windowStart, cursorPos);
    const m = before.match(/(^|\s)@([^\s@]{0,24})$/);
    if (!m) {
      closeCommentMention();
      return;
    }
    const atOffset = before.lastIndexOf('@');
    if (atOffset < 0) {
      closeCommentMention();
      return;
    }
    const query = m[2] ?? '';
    setCommentMentionQuery(query);
    setCommentMentionStart(windowStart + atOffset);
    setCommentMentionCursor(cursorPos);
    setCommentMentionOpen(true);
    setCommentMentionActiveIndex(0);
  };

  const insertCommentMention = (u: AssignToUser) => {
    if (commentMentionStart == null || commentMentionCursor == null) return;
    const before = commentDraft.slice(0, commentMentionStart);
    const after = commentDraft.slice(commentMentionCursor);
    const insert = `@${u.name} `;
    const next = `${before}${insert}${after}`;
    const nextCursor = before.length + insert.length;

    setCommentDraft(next);
    closeCommentMention();

    window.setTimeout(() => {
      const el = commentInputRef.current as any;
      if (el && typeof el.setSelectionRange === 'function') {
        el.focus();
        el.setSelectionRange(nextCursor, nextCursor);
      }
    }, 0);
  };



  const fetchComments = useCallback(async (taskId: number) => {
    setCommentsLoading(true);
    try {
      // New API: fetch task detail which includes comments
      const res = await apiClient.get(`/api/v1/delivery/tasks/${taskId}`);
      const data = res.data;

      // Try to extract comments from common response shapes
      const arr =
        (data && Array.isArray(data.comments) && data.comments) ||
        (data && Array.isArray(data.items) && data.items) ||
        (Array.isArray(data) ? data : []);

      setComments(arr as any);
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to load comments';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const handleOpenComments = async (note: Note) => {
    setCommentNote(note);
    setView('comments');
    setCommentDraft('');
    await fetchComments(note.id);
  };

  const handleSubmitComment = async () => {
    if (!commentNote) return;
    const text = commentDraft.trim();
    if (!text) return;

    const taskId = commentNote.id;
    // optimistic append
    const optimistic: NoteComment = {
      id: `tmp_${Date.now()}`,
      task_id: taskId,
      comment_text: text,
      comment: text,
      created_at: new Date().toISOString(),
      created_by_name: 'You',
    };
    setComments((prev) => [...prev, optimistic]);
    setCommentDraft('');

    try {
      const url = `/api/v1/delivery/tasks/${taskId}/comments`;
      // New API expects { comment_text: string }
      await apiClient.post(url, { comment_text: text });
      await fetchComments(taskId);
      await fetchNotes();
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to add comment';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      // rollback optimistic
      setComments((prev) => prev.filter((c) => c.id !== optimistic.id));
      setCommentDraft(text);
    }
  };

  const closeMention = () => {
    setMentionOpen(false);
    setMentionQuery('');
    setMentionStartIndex(null);
    setMentionPos(null);
    setMentionActiveIndex(0);
  };

  const insertMention = (u: AssignToUser) => {
    const q = quillRef.current?.getEditor();
    if (!q) return;
    const sel = q.getSelection();
    if (!sel || mentionStartIndex == null) return;

    const at = mentionStartIndex;
    const len = sel.index - mentionStartIndex;
    if (len > 0) q.deleteText(at, len, 'user');

    const label = `@${u.name}`;
    q.insertText(at, label, { link: `user:${u.id}` }, 'user');
    q.insertText(at + label.length, ' ', 'user');
    q.setSelection(at + label.length + 1, 0, 'user');
    closeMention();
  };

  // Mention detection + keyboard navigation
  useEffect(() => {
    if (view !== 'form') {
      closeMention();
      return;
    }
    const q = quillRef.current?.getEditor();
    if (!q) return;

    const updateMentionFromCursor = () => {
      const sel = q.getSelection();
      if (!sel) {
        closeMention();
        return;
      }

      const cursor = sel.index;
      const windowStart = Math.max(0, cursor - 48);
      const before = q.getText(windowStart, cursor - windowStart);
      const m = before.match(/(^|\s)@([^\s@]{0,24})$/);
      if (!m) {
        closeMention();
        return;
      }

      const atOffset = before.lastIndexOf('@');
      if (atOffset < 0) {
        closeMention();
        return;
      }

      const query = m[2] ?? '';
      const bounds = q.getBounds(cursor);
      const wrap = mentionWrapRef.current;
      if (!wrap || !bounds) return;

      setMentionQuery(query);
      setMentionStartIndex(windowStart + atOffset);
      setMentionPos({ top: bounds.bottom + 6, left: bounds.left });
      setMentionOpen(true);
      setMentionActiveIndex(0);
    };

    const onTextChange = () => updateMentionFromCursor();
    const onSelectionChange = () => updateMentionFromCursor();

    const onKeyDown = (e: KeyboardEvent) => {
      if (!mentionOpen) return;
      if (!mentionCandidates.length) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeMention();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionActiveIndex((i) => Math.min(i + 1, mentionCandidates.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const u = mentionCandidates[mentionActiveIndex];
        if (u) insertMention(u);
        return;
      }
    };

    q.on('text-change', onTextChange);
    q.on('selection-change', onSelectionChange);
    q.root.addEventListener('keydown', onKeyDown);

    return () => {
      q.off('text-change', onTextChange);
      q.off('selection-change', onSelectionChange);
      q.root.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, mentionOpen, mentionCandidates, mentionActiveIndex]);

  useEffect(() => {
    if (view !== 'comments') return;
    // keep latest message visible
    const t = window.setTimeout(() => {
      commentsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [view, comments.length]);

  const handleToggleStatus = async (note: Note) => {
    if (!permissions.canUpdate) return;
    const type = (note.task_type || note.note_type) as any;
    if (!(type === 'action_item' || type === 'issue')) return;

    const current = normalizeStatus(note.status);
    const next: NoteStatus = current === 'closed' ? 'open' : 'closed';

    // Optimistic update
    setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, status: next } : n)));
    try {
      await apiClient.patch(`/api/v1/delivery/tasks/update_status/${note.id}`, {
        status: next,
      });
      await fetchNotes();
    } catch (err: any) {
      // Rollback
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, status: current } : n)));
      const msg = err.response?.data?.detail || err.message || 'Failed to update status';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handleSetStatus = async (note: Note, next: NoteStatus) => {
    if (!permissions.canUpdate) return;
    const type = (note.task_type || note.note_type) as any;
    if (!(type === 'action_item' || type === 'issue')) return;
    if (next !== 'open' && next !== 'closed') return;

    const current = normalizeStatus(note.status);
    if (current === next) return;

    // Optimistic update
    setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, status: next } : n)));
    try {
      await apiClient.patch(`/api/v1/delivery/tasks/update_status/${note.id}`, {
        status: next,
      });
      await fetchNotes();
    } catch (err: any) {
      // Rollback
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, status: current } : n)));
      const msg = err.response?.data?.detail || err.message || 'Failed to update status';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const handleClosePanel = () => {
    setView('list');
    setEditingNote(null);
    setCommentNote(null);
    onClose?.();
  };

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5, md: 3 },
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {view === 'form' || view === 'comments' ? (
            <IconButton
              size="small"
              onClick={() => {
                setView('list');
                setEditingNote(null);
                setCommentNote(null);
              }}
              sx={{ border: '1px solid', borderColor: 'divider' }}
              aria-label="Back to action items list"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          ) : null}

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.88rem', sm: '0.95rem', md: '1rem' } }} noWrap>
              {view === 'comments'
                ? 'Comments'
                : view === 'form'
                  ? (editingNote ? 'Edit Action Item' : 'Create Action Item')
                  : 'Action Items'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
              {view === 'comments' ? (commentNote?.title || page) : page}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {view === 'list' ? (
            <Button
              disabled={!permissions.canCreate}
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNote}
              size="small"
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
            >
              Add Action Item
            </Button>
          ) : null}

          <IconButton
            onClick={handleClosePanel}
            size="small"
            sx={{ border: '1px solid', borderColor: 'divider' }}
            aria-label="Close notes"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

        {error && (
          <Box sx={{ px: { xs: 2, sm: 2.5, md: 3 }, pt: 2 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 2, sm: 2.5, md: 3 },
            py: 2,
          }}
        >
          {view === 'list' ? (
            <>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
                  <CircularProgress />
                </Box>
              ) : notes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <NoteIcon sx={{ fontSize: { xs: 28, sm: 36, md: 44 }, color: '#d1d5db', mb: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    No notes found for this date range.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Click “Add Note” to create your first note.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                  {notes
                    .filter((note) => {
                      if (!statusFilter) return true;
                      const st = normalizeStatus(note.status);
                      if (statusFilter === 'open') return st === 'open';
                      if (statusFilter === 'closed') return st === 'closed';
                      return true;
                    })
                    .map((note) => {
                      const type = String(note.task_type || note.note_type || 'general');
                      const preview = truncate(stripHtml(note.description || ''), 170);
                      const st = normalizeStatus(note.status);
                      const assigneeName = getAssignToName(note);
                      const openedBy = note.created_by_name ? String(note.created_by_name) : '';
                      const filterLabel = formatDateRange(note.start_date, note.end_date);

                      const statusSelect = (type === 'action_item' || type === 'issue') ? (
                        <FormControl size="small" sx={{ maxWidth: 190 }}>
                          <Select
                            value={(st || '') as any}
                            displayEmpty
                            onChange={(e) => handleSetStatus(note, e.target.value as NoteStatus)}
                            disabled={!permissions.canUpdate}
                            renderValue={(val) => {
                              const v = String(val || '');
                              const isOpen = v === 'open';
                              const isClosed = v === 'closed';
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  {/* <Typography sx={{ fontWeight: 900, fontSize: '0.82rem', color: '#0f172a' }}>
                                    Status:
                                  </Typography> */}
                                  <Typography
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.78rem',
                                      color: isOpen ? 'success.dark' : isClosed ? '#0f172a' : 'text.secondary',
                                    }}
                                  >
                                    {isOpen ? 'Open' : isClosed ? 'Closed' : '—'}
                                  </Typography>
                                </Box>
                              );
                            }}
                            sx={{
                              height: 34,
                              borderRadius: 999,
                              px: 0.25,
                              width: '100%',
                              bgcolor:
                                st === 'open'
                                  ? 'rgba(16, 185, 129, 0.12)'
                                  : st === 'closed'
                                    ? 'rgba(239, 68, 68, 0.10)'
                                    : 'rgba(148, 163, 184, 0.10)',
                              '& .MuiSelect-select': { py: 0.6, pr: 3, display: 'flex', alignItems: 'center' },
                              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(226, 232, 240, 0.9)' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148, 163, 184, 0.7)' },
                            }}
                          >
                            <MenuItem value="open">
                              <Typography sx={{ fontWeight: 500, fontSize: '0.78rem', color: 'success.main' }}>Open</Typography>
                            </MenuItem>
                            <MenuItem value="closed">
                              <Typography sx={{ fontWeight: 500, fontSize: '0.78rem', color: 'error.main' }}>Closed</Typography>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      ) : null;

                      const actionIcons = (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Add comment" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenComments(note)}
                              aria-label="Add comment"
                              sx={{
                                color: 'rgba(71, 85, 105, 0.95)',
                                '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.05)' },
                              }}
                            >
                              <ChatBubbleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEditNote(note)}
                              aria-label="Edit"
                              sx={{
                                color: 'rgba(71, 85, 105, 0.95)',
                                '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.05)' },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteNote(note.id)}
                              aria-label="Delete"
                              disabled={!permissions.canDelete}
                              sx={{
                                color: 'rgba(71, 85, 105, 0.95)',
                                '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.05)' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );

                      return (
                        <Card
                          key={note.id}
                          variant="outlined"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleEditNote(note)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleEditNote(note);
                            }
                          }}
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            borderRadius: 3,
                            borderColor: 'rgba(226, 232, 240, 0.95)',
                            boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08)',
                            bgcolor: '#fff',
                            cursor: 'pointer',
                            transition: 'box-shadow 160ms ease, transform 160ms ease',
                            '&:hover': {
                              boxShadow: '0 18px 36px rgba(15, 23, 42, 0.10)',
                              transform: 'translateY(-1px)',
                            },
                            '&:focus-visible': {
                              outline: '2px solid rgba(30, 64, 175, 0.6)',
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              p: { xs: 1.75, sm: 2.25 },
                              display: 'grid',
                              gridTemplateAreas: {
                                xs: `"icon title" "meta meta" "footer footer"`,
                                sm: `"icon title status" "icon meta actions"`,
                              },
                              gridTemplateColumns: { xs: '56px minmax(0, 1fr)', sm: '64px minmax(0, 1fr) auto' },
                              columnGap: { xs: 1.25, sm: 1.75 },
                              rowGap: 1.25,
                              alignItems: 'center',
                              minWidth: 0,
                            }}
                          >
                            {/* icon */}
                            <Box sx={{ gridArea: 'icon' }}>
                              <Box
                                sx={{
                                  width: { xs: 52, sm: 56 },
                                  height: { xs: 52, sm: 56 },
                                  borderRadius: '50%',
                                  display: 'grid',
                                  placeItems: 'center',
                                  bgcolor:
                                    type === 'action_item'
                                      ? 'rgba(16, 185, 129, 0.16)'
                                      : type === 'issue'
                                        ? 'rgba(239, 68, 68, 0.14)'
                                        : 'rgba(99, 102, 241, 0.10)',
                                  border: '1px solid',
                                  borderColor:
                                    type === 'action_item'
                                      ? 'rgba(16, 185, 129, 0.28)'
                                      : type === 'issue'
                                        ? 'rgba(239, 68, 68, 0.26)'
                                        : 'rgba(148, 163, 184, 0.35)',
                                  '& svg': { fontSize: 30 },
                                }}
                              >
                                {getNoteTypeIcon(type)}
                              </Box>
                            </Box>

                            {/* title */}
                            <Box sx={{ gridArea: 'title', minWidth: 0 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.2 }}
                                noWrap
                              >
                                {note.title}
                              </Typography>
                              {assigneeName ? (
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', color: 'text.secondary', mt: 0.25, fontWeight: 600 }}
                                  noWrap
                                >
                                  Assign To: {assigneeName}
                                </Typography>
                              ) : null}
                            </Box>

                            {/* status (desktop) */}
                            <Box
                              sx={{
                                gridArea: 'status',
                                justifySelf: 'end',
                                display: { xs: 'none', sm: 'block' },
                                minWidth: 0,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {statusSelect}
                            </Box>

                            {/* meta */}
                            <Box sx={{ gridArea: 'meta' }}>
                              {openedBy ? (
                                <Typography variant="body2" sx={{fontSize: '0.78rem', color: 'text.secondary', fontWeight: 500 }}>
                                  Opened By: <Box component="span" sx={{ fontWeight: 500, fontSize: '0.78rem', color: 'text.secondary' }}>{openedBy}</Box>
                                </Typography>
                              ) : null}
                              <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                Filter: {filterLabel}
                              </Typography>
                              {preview ? (
                                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.75 }}>
                                  {preview}
                                </Typography>
                              ) : null}
                            </Box>

                            {/* actions (desktop) */}
                            <Box
                              sx={{ gridArea: 'actions', justifySelf: 'end', display: { xs: 'none', sm: 'flex' } }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Box sx={{ flexShrink: 0 }}>{actionIcons}</Box>
                            </Box>

                            {/* footer (mobile) */}
                            <Box
                              sx={{
                                gridArea: 'footer',
                                display: { xs: 'flex', sm: 'none' },
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Box sx={{ flex: 1, minWidth: 0, maxWidth: 220 }}>{statusSelect}</Box>
                              {actionIcons}
                            </Box>
                          </Box>
                        </Card>
                      );
                    })}
                </Box>
              )}
            </>
          ) : view === 'comments' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: 1.5,
                  mb: 1.5,
                  bgcolor: 'rgba(248, 250, 252, 0.9)',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`#${commentNote?.id ?? ''}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: { xs: '0.56rem', sm: '0.66rem' }, fontWeight: 600 } }}
                  />
                  {commentNote?.task_type || commentNote?.note_type ? (
                    <Chip
                      label={String(commentNote.task_type || commentNote.note_type).replace('_', ' ')}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: { xs: '0.56rem', sm: '0.66rem' } } }}
                    />
                  ) : null}
                  {normalizeStatus(commentNote?.status) ? (
                    <Chip
                      label={normalizeStatus(commentNote?.status) === 'open' ? 'Open' : 'Closed'}
                      size="small"
                      color={normalizeStatus(commentNote?.status) === 'open' ? 'success' : 'default'}
                      variant={normalizeStatus(commentNote?.status) === 'open' ? 'filled' : 'outlined'}
                      sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: { xs: '0.56rem', sm: '0.66rem' } } }}
                    />
                  ) : null}
                </Box>
                {commentNote?.description ? (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                    {truncate(stripHtml(commentNote.description), 160) || '—'}
                  </Typography>
                ) : null}
              </Paper>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  pr: 0.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  pb: 1,
                }}
              >
                {commentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : comments.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      No comments yet.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Add the first comment below.
                    </Typography>
                  </Box>
                ) : (
                  comments
                    .map((c) => {
                      const text = parseCommentText(c);
                      const who = c.created_by_name || 'User';
                      const when = c.created_at ? new Date(c.created_at).toLocaleString() : '';
                      if (!text) return null;
                      return (
                        <Box key={String(c.id)} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box
                            sx={{
                              alignSelf: 'flex-start',
                              maxWidth: '92%',
                              bgcolor: '#eff6ff',
                              border: '1px solid #bfdbfe',
                              borderRadius: 2,
                              px: 1.25,
                              py: 0.9,
                            }}
                          >
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, mb: 0.25 }}>
                              {who}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {text}
                            </Typography>
                          </Box>
                          {when ? (
                            <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'flex-start' }}>
                              {when}
                            </Typography>
                          ) : null}
                        </Box>
                      );
                    })
                    .filter(Boolean)
                )}
                <div ref={commentsEndRef} />
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  borderRadius: 2,
                  p: 1,
                  borderColor: 'divider',
                  bgcolor: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    inputRef={commentInputRef}
                    value={commentDraft}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCommentDraft(val);
                      const cursor = typeof (e.target as any).selectionStart === 'number'
                        ? Number((e.target as any).selectionStart)
                        : val.length;
                      updateCommentMentionFromText(val, cursor);
                    }}
                    placeholder="Write a comment…"
                    multiline
                    minRows={1}
                    maxRows={4}
                    fullWidth
                    onKeyDown={(e) => {
                      if (commentMentionOpen) {
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          closeCommentMention();
                          return;
                        }
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setCommentMentionActiveIndex((i) => Math.min(i + 1, commentMentionCandidates.length - 1));
                          return;
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setCommentMentionActiveIndex((i) => Math.max(i - 1, 0));
                          return;
                        }
                        if ((e.key === 'Enter' || e.key === 'Tab') && !(e.ctrlKey || e.metaKey)) {
                          const u = commentMentionCandidates[commentMentionActiveIndex];
                          if (u) {
                            e.preventDefault();
                            insertCommentMention(u);
                            return;
                          }
                        }
                      }
                      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': { borderRadius: 2 },
                    }}
                  />

                  <Popper
                    open={commentMentionOpen && commentMentionCandidates.length > 0}
                    anchorEl={commentInputRef.current}
                    placement="top-start"
                    modifiers={[{ name: 'offset', options: { offset: [0, 8] } }] as any}
                    sx={{ zIndex: 2000 }}
                  >
                    <ClickAwayListener onClickAway={closeCommentMention}>
                      <Paper
                        elevation={8}
                        sx={{
                          width: 280,
                          maxHeight: 220,
                          overflowY: 'auto',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                        onMouseDown={(ev) => {
                          // Keep textarea focus when selecting a mention
                          ev.preventDefault();
                        }}
                      >
                        <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                            Mention user ({commentMentionQuery ? `“${commentMentionQuery}”` : 'type a name'})
                          </Typography>
                        </Box>
                        <List dense disablePadding>
                          {commentMentionCandidates.map((u, idx) => (
                            <ListItemButton
                              key={u.id}
                              selected={idx === commentMentionActiveIndex}
                              onClick={() => insertCommentMention(u)}
                              sx={{
                                py: 0.75,
                                '&.Mui-selected': { bgcolor: 'rgba(30, 64, 175, 0.08)' },
                                '&.Mui-selected:hover': { bgcolor: 'rgba(30, 64, 175, 0.12)' },
                              }}
                            >
                              <ListItemText
                                primary={u.name}
                                secondary={`@${u.name}`}
                                primaryTypographyProps={{ fontWeight: 700, fontSize: '0.88rem' }}
                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                  <Tooltip title="Send (Ctrl+Enter)" arrow>
                    <span>
                      <IconButton
                        color="primary"
                        onClick={handleSubmitComment}
                        disabled={!commentDraft.trim() || !commentNote}
                        aria-label="Send comment"
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                fullWidth
                required
              />

              <FormControl fullWidth>
                <InputLabel>Note Type</InputLabel>
                <Select
                  value={formData.note_type}
                  onChange={(e) => setFormData({ ...formData, note_type: e.target.value as any })}
                  label="Note Type"
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="action_item">Action Item</MenuItem>
                  <MenuItem value="issue">Issue/Concern</MenuItem>
                  <MenuItem value="achievement">Achievement</MenuItem>
                </Select>
              </FormControl>

              {(formData.note_type === "action_item" || formData.note_type === "issue") && (
                <FormControl fullWidth>
                  <InputLabel>Assign To</InputLabel>
                  <Select
                    value={formData.assign_to}
                    onChange={(e) => setFormData({ ...formData, assign_to: e.target.value as any })}
                    label="Assign To"
                    disabled={assignToLoading || !!assignToError}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {assignToUsers.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {assignToLoading ? 'Loading users…' : assignToError ? 'Unable to load users' : ''}
                  </FormHelperText>
                </FormControl>
              )}

              {/* {(formData.note_type === "action_item" || formData.note_type === "issue") && (
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as NoteStatus })}
                    label="Status"
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              )} */}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Description
                </Typography>
                <Box
                  ref={mentionWrapRef}
                  sx={{
                    position: 'relative',
                    '& .ql-toolbar.ql-snow': { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
                    '& .ql-container.ql-snow': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 },
                    '& .ql-container': { height: 220 },
                    '& .ql-editor': { fontFamily: 'inherit', fontSize: '0.95rem' },
                    '& .ql-editor a[href^="user:"]': {
                      backgroundColor: 'rgba(16, 185, 129, 0.12)',
                      border: '1px solid rgba(16, 185, 129, 0.35)',
                      borderRadius: 6,
                      padding: '0 6px',
                      textDecoration: 'none',
                      color: '#047857',
                      fontWeight: 700,
                    },
                  }}
                >
                  <ReactQuill
                    ref={quillRef as any}
                    value={formData.description}
                    onChange={(content) => setFormData({ ...formData, description: content })}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['link'],
                        ['clean'],
                      ],
                    }}
                  />

                  {mentionOpen && mentionPos ? (
                    <Paper
                      elevation={6}
                      sx={{
                        position: 'absolute',
                        top: mentionPos.top,
                        left: mentionPos.left,
                        minWidth: 220,
                        maxWidth: 280,
                        maxHeight: 240,
                        overflowY: 'auto',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        zIndex: 20,
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                          Mention user ({mentionQuery ? `“${mentionQuery}”` : 'type a name'})
                        </Typography>
                      </Box>
                      {mentionCandidates.length === 0 ? (
                        <Box sx={{ px: 1.25, py: 1.25 }}>
                          <Typography variant="body2" color="text.secondary">
                            No matches
                          </Typography>
                        </Box>
                      ) : (
                        mentionCandidates.map((u, idx) => (
                          <Box
                            key={u.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => insertMention(u)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') insertMention(u);
                            }}
                            sx={{
                              px: 1.25,
                              py: 1,
                              cursor: 'pointer',
                              bgcolor: idx === mentionActiveIndex ? 'rgba(30, 64, 175, 0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(30, 64, 175, 0.08)' },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {u.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              @{u.name}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Paper>
                  ) : null}

                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                    Tip: type <b>@</b> to mention a user in the description. Use ↑/↓ and Enter to select.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer (sticky) */}
        {view === 'form' ? (
          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              bottom: 0,
              bgcolor: '#fff',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            <Button variant="outlined" onClick={handleClosePanel} sx={{ textTransform: 'none', borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNote}
              variant="contained"
              disabled={!permissions.canCreate || !permissions.canUpdate}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 800 }}
            >
              {editingNote ? 'Update' : 'Create'}
            </Button>
          </Box>
        ) : null}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Action item?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this action item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotesPanel;
